import React from "react";
import LegalLayout from "@/components/legal/LegalLayout";

export default function PrivacyPolicy() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="1 March 2026" version="1.0">
      <p>
        This Privacy Policy explains how <strong>Connexis Group Ltd</strong> ("Connexis", "we", "our", "us"), a
        company registered in England and Wales with company number <strong>17214446</strong>, registered office at
        71-75 Shelton Street, Covent Garden, London WC2H 9JQ, United Kingdom, collects and processes personal data
        when you visit our website at <a href="https://connex.is">connex.is</a>, use our platform, apply to become
        a Publisher or Affiliate, or are a financial-services business that purchases enquiries through our routing
        infrastructure.
      </p>

      <h2>1. Who we are and our role</h2>
      <p>
        Connexis operates a B2B routing infrastructure that connects financial-services consumer enquiries with
        FCA-aware buyers in real time. We act as a <strong>data controller</strong> for our website visitors, account
        holders, applicants and prospective customers. For consumer enquiries routed through our infrastructure to
        buyer endpoints, we act as a <strong>joint controller</strong> with the receiving buyer, and as a{" "}
        <strong>data processor</strong> with respect to subsequent storage on the buyer's behalf where applicable.
      </p>

      <h2>2. The data we collect</h2>

      <h3>2.1 Account holder data</h3>
      <ul>
        <li>Identification: full name, business email address, company name, role.</li>
        <li>Authentication: hashed password, two-factor codes, session tokens.</li>
        <li>Communications: support tickets, in-portal chat, contracts you sign with us.</li>
        <li>Billing: VAT number, billing address, payment-method tokens (handled by Stripe — we do not store full card numbers).</li>
      </ul>

      <h3>2.2 Lead / enquiry data</h3>
      <ul>
        <li>Contact details: first name, last name, email, mobile phone number, postal code.</li>
        <li>Enquiry context: sector of interest, affordability band, intent text, consent token, TrustedForm certificate URL.</li>
        <li>Technical metadata: IP address, user-agent, source publisher identifier, timestamp.</li>
      </ul>

      <h3>2.3 Website visitor data</h3>
      <ul>
        <li>Strictly necessary cookies (session, CSRF protection).</li>
        <li>Analytics cookies (only after consent) — aggregate usage data.</li>
        <li>Server logs: IP, page accessed, referrer (retained 30 days for security).</li>
      </ul>

      <h2>3. Lawful basis for processing</h2>
      <ul>
        <li>
          <strong>Contract (UK GDPR Art. 6(1)(b))</strong>: processing necessary to provide the platform to account
          holders and fulfil our agreements.
        </li>
        <li>
          <strong>Consent (Art. 6(1)(a))</strong>: lead data is processed on the basis of explicit, recorded consent
          obtained at point of capture, evidenced by TrustedForm or equivalent consent infrastructure.
        </li>
        <li>
          <strong>Legitimate interest (Art. 6(1)(f))</strong>: fraud prevention, security monitoring, B2B marketing
          to business contacts, platform analytics — balanced against your rights.
        </li>
        <li>
          <strong>Legal obligation (Art. 6(1)(c))</strong>: AML/KYB checks, accounting record retention, FCA-related
          record-keeping.
        </li>
      </ul>

      <h2>4. How we use your data</h2>
      <ul>
        <li>Operating the routing infrastructure (matching enquiries to buyers in sub-second).</li>
        <li>Providing access to your portal (buyer dashboard, publisher dashboard, affiliate dashboard).</li>
        <li>Processing payments (Stripe), settling publisher payouts (Stripe Connect or bank transfer).</li>
        <li>Sending transactional emails and SMS (account lifecycle, lead confirmations, contract signing).</li>
        <li>Compliance, audit and dispute resolution.</li>
        <li>Service improvement, security monitoring, fraud prevention.</li>
      </ul>

      <h2>5. Sharing &amp; sub-processors</h2>
      <p>We share personal data with carefully selected sub-processors that are bound by Data Processing Agreements:</p>
      <ul>
        <li><strong>MongoDB Atlas</strong> — primary database (EU-West).</li>
        <li><strong>Stripe Payments UK Ltd</strong> — card payments + Connect payouts.</li>
        <li><strong>Anthropic, OpenAI, Google</strong> — AI inference for qualification and analytics (data minimised, no training).</li>
        <li><strong>Cloudflare</strong> — DDoS / WAF / CDN edge.</li>
      </ul>
      <p className="!text-[13.5px] !text-slate-600">
        Operational vendors used for transactional email and SMS notifications are listed in our{" "}
        <a href="/legal/dpa">Data Processing Agreement</a> and available to account holders on request to{" "}
        <a href="mailto:legal@connex.is">legal@connex.is</a>.
      </p>
      <p>
        We share enquiry data with a single <strong>matched buyer</strong> in real time at the point of routing. Each
        enquiry is delivered to exactly one buyer (exclusive, never resold). The buyer becomes joint controller of
        that enquiry data and is bound by their own privacy obligations.
      </p>

      <h2>6. International transfers</h2>
      <p>
        Some sub-processors are located outside the UK. Where this is the case, we rely on the UK International Data
        Transfer Agreement, UK Addendum to the EU SCCs, or adequacy decisions (e.g. EU/EEA). Specific transfer
        mechanisms per processor are listed in our <a href="/legal/dpa">Data Processing Agreement</a>.
      </p>

      <h2>7. Retention</h2>
      <ul>
        <li><strong>Account data</strong>: for the lifetime of your account, plus 7 years (UK Companies Act).</li>
        <li><strong>Lead data</strong>: 24 months from delivery, then purged or anonymised, unless your buyer retention agreement specifies a different window.</li>
        <li><strong>Server logs</strong>: 30 days.</li>
        <li><strong>Invoices &amp; ledger</strong>: 7 years (HMRC).</li>
        <li><strong>Audit / compliance events</strong>: 6 years.</li>
      </ul>

      <h2>8. Your rights (UK GDPR)</h2>
      <p>You have the right to:</p>
      <ul>
        <li>Access your personal data (subject access request).</li>
        <li>Rectify inaccurate data.</li>
        <li>Erasure ("right to be forgotten"), subject to legal-retention obligations.</li>
        <li>Restrict or object to processing.</li>
        <li>Data portability.</li>
        <li>Withdraw consent at any time (without affecting prior lawful processing).</li>
        <li>Lodge a complaint with the Information Commissioner's Office (ICO) at <a href="https://ico.org.uk">ico.org.uk</a>.</li>
      </ul>
      <p>
        To exercise these rights, email <a href="mailto:legal@connex.is">legal@connex.is</a>. We respond within 30
        days.
      </p>

      <h2>9. Security</h2>
      <p>
        We implement industry-standard technical and organisational measures: TLS 1.3 in transit, AES-256 at rest,
        principle-of-least-privilege access control, audited admin actions, secrets management via environment
        isolation, automated daily backups, and continuous vulnerability scanning. Detailed measures are documented
        in our <a href="/trust">Trust Center</a>.
      </p>

      <h2>10. Changes to this policy</h2>
      <p>
        Material changes will be notified to account holders by email and SMS (where opted in) at least 14 days
        before they take effect. The version number and "last updated" date above always reflect the current
        version.
      </p>

      <h2>11. Contact</h2>
      <p>
        Data Protection contact: <a href="mailto:legal@connex.is">legal@connex.is</a>
        <br />
        Postal: Connexis Group Ltd, 71-75 Shelton Street, Covent Garden, London WC2H 9JQ, United Kingdom
      </p>
    </LegalLayout>
  );
}
